import { lazy, Suspense, useState } from 'react'
import { Link } from 'react-router-dom'
import { IconArrowDown, IconArrowRight } from '../ui/Icon'
import useAfterLoad from '../../hooks/useAfterLoad'
import useReducedMotion from '../../hooks/useReducedMotion'
import './service-hero.css'

/* The drawings load on their own, after the page: they are in neither the
   prerender nor the first client render. */
const HeroScene = lazy(() => import('./hero/HeroScene'))

function PauseGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M4.5 3h2.5v10H4.5zM9 3h2.5v10H9z" fill="currentColor" />
    </svg>
  )
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M5 2.75v10.5L13.5 8z" fill="currentColor" />
    </svg>
  )
}

/**
 * The operator at work for this process (hero/), drawn straight on the
 * page and standing on the hero's bottom rule: no sheet, no frame.
 * The drawing is decorative (the h1 carries the name); its pause key sits
 * in the stage's top-left corner (WCAG 2.2.2). The drawing is laid onto the page
 * once it has loaded and gone idle, so it never competes with the first
 * paint (the prerender keeps its place, empty, and no key); under reduced
 * motion it holds its still until the reader presses play.
 *
 * @param {{
 *   service: 'pad' | 'screen' | 'assembly',
 *   name: string,
 *   controls: { pause: string, play: string },
 * }} props
 */
function Scene({ service, name, controls }) {
  const reducedMotion = useReducedMotion()
  const isPrerender = typeof window !== 'undefined' && Boolean(window.__ANKA_PRERENDER__)
  const loaded = useAfterLoad()
  // null follows the motion preference; a press of the key overrides it.
  const [choice, setChoice] = useState(null)
  const playing = !isPrerender && (choice === null ? !reducedMotion : choice === 'play')
  const live = !isPrerender && (choice !== null || (loaded && !reducedMotion))

  return (
    <div className="sp-scene">
      <div className="sp-scene-stage" aria-hidden="true">
        {loaded && !isPrerender && (
          <Suspense fallback={null}>
            <HeroScene service={service} live={live} paused={live && !playing} />
          </Suspense>
        )}
      </div>
      {!isPrerender && (
        <button
          type="button"
          className="sp-scene-key"
          aria-label={`${playing ? controls.pause : controls.play}: ${name}`}
          onClick={() => setChoice(playing ? 'pause' : 'play')}
        >
          {playing ? <PauseGlyph /> : <PlayGlyph />}
        </button>
      )}
    </div>
  )
}

/**
 * Service hero on the sweep: breadcrumb legends, the h1 (service title
 * verbatim, former accent span on its own line), the lead and the two CTAs,
 * beside the operator's scene.
 *
 * @param {{
 *   service: 'pad' | 'screen' | 'assembly',
 *   hero: { titleLead: string, titleTail: string, lead: string },
 *   chrome: object,
 * }} props
 */
export default function ServiceHero({ service, hero, chrome }) {
  const name = chrome.names[service]

  return (
    <section className="sp-hero" aria-labelledby="sp-hero-title">
      <div className="container sp-hero-grid">
        <div className="sp-hero-copy">
          <nav className="sp-crumbs" aria-label={chrome.crumbLabel}>
            <ol>
              <li>
                <Link to={chrome.homeTo} className="legend sp-crumb" viewTransition>
                  {chrome.home}
                </Link>
              </li>
              <li>
                <a href={chrome.solutionsHref} className="legend sp-crumb">
                  {chrome.solutions}
                </a>
              </li>
              <li>
                <span className="legend sp-crumb" aria-current="page">
                  {name}
                </span>
              </li>
            </ol>
          </nav>

          <h1 className="sp-hero-title" id="sp-hero-title">
            <span className="sp-hero-title-lead">{hero.titleLead}</span>{' '}
            <span className="sp-hero-title-tail">{hero.titleTail}</span>
          </h1>

          <p className="lead sp-hero-lead">{hero.lead}</p>

          <div className="sp-hero-actions">
            <a href="#contact" className="btn">
              {chrome.ctaPrimary}
              <IconArrowRight />
            </a>
            <a href="#advantages" className="btn btn--ghost">
              {chrome.ctaSecondary}
              <IconArrowDown />
            </a>
          </div>
        </div>

        <Scene service={service} name={name} controls={chrome.sceneControls} />
      </div>
    </section>
  )
}
